import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Shield, Globe, Monitor, Brain, Package, Check, X as XIcon, ChevronRight } from 'lucide-react';
import { MOCK_TESTIMONIALS } from '@/lib/mock-api';
import { api } from '@/lib/api-client';
import { SEOHead, JsonLdSchema } from '@/components/seo';
import { PAGE_SEO } from '@/lib/seo-config';

// FAQ data for structured data
const landingFAQ = [
  { question: 'What is ClawStinger?', answer: 'ClawStinger is a managed hosting platform for OpenClaw AI agents. Deploy Claude, GPT, and other AI assistants in under 60 seconds without any server configuration.' },
  { question: 'How fast can I deploy an OpenClaw agent?', answer: 'Deployment takes under 60 seconds. Just select your AI model, connect your messaging platform (Telegram, Discord, etc.), and click deploy.' },
  { question: 'What is OpenClaw?', answer: 'OpenClaw is an open-source framework for building AI assistants that can interact through various messaging platforms. ClawStinger provides managed hosting for OpenClaw.' },
  { question: 'Do I need technical knowledge to use ClawStinger?', answer: 'No! ClawStinger handles all server configuration, security, and maintenance. You just configure your AI agent through our dashboard.' },
  { question: 'What AI models are supported?', answer: 'ClawStinger supports Claude (Opus, Sonnet, Haiku), GPT-4, GPT-3.5, and other popular AI models. You can switch models anytime.' },
];

const TerminalDemo = () => (
  <div className="terminal-window max-w-2xl mx-auto">
    <div className="terminal-header">
      <div className="terminal-dot bg-red-500/80" />
      <div className="terminal-dot bg-yellow-500/80" />
      <div className="terminal-dot bg-green-500/80" />
      <span className="text-xs text-zinc-500 ml-2 font-mono">clawstinger deploy</span>
    </div>
    <div className="terminal-body">
      <div><span className="prompt">$</span> <span className="command">clawstinger deploy --model claude-opus --channel telegram</span></div>
      <div className="mt-2 output">Provisioning VPS in us-east-1...</div>
      <div className="output">Installing OpenClaw v0.8.2...</div>
      <div className="output">Configuring Claude Opus 4.5...</div>
      <div className="output">Connecting Telegram bot...</div>
      <div className="output">Running health checks...</div>
      <div className="mt-2 success">Your OpenClaw agent is live at 45.33.21.100</div>
      <div className="success">RDP access ready. Total time: 47 seconds.</div>
      <div className="mt-2"><span className="prompt">$</span> <span className="animate-blink">_</span></div>
    </div>
  </div>
);

const features = [
  { icon: Zap, title: '60-Second Deploy', desc: 'Pick your model, connect messaging, deploy. No SSH, no server config.' },
  { icon: Monitor, title: 'RDP Access', desc: 'Full desktop access to your instance. Configure anything via browser.' },
  { icon: Brain, title: 'Multi-Agent', desc: 'Run up to 20 agents per instance. Scale as your needs grow.' },
  { icon: Shield, title: '99.9% Uptime SLA', desc: 'Enterprise-grade infrastructure with automated backups.' },
  { icon: Globe, title: '8 Global Regions', desc: 'Deploy close to your users. US, EU, Asia, and more.' },
  { icon: Package, title: 'Skills Marketplace', desc: 'One-click install from 100+ community-built extensions.' },
];

const comparisonData = [
  { feature: 'Setup Time', cs: 'Under 60s', diy: '~60 min', sc: 'Under 1 min', mc: '~5 min' },
  { feature: 'RDP Access', cs: true, diy: true, sc: false, mc: false },
  { feature: 'Multi-Agent', cs: 'Up to 20', diy: 'Manual', sc: '1 agent', mc: '3 agents' },
  { feature: 'Tiered Plans', cs: true, diy: false, sc: false, mc: true },
  { feature: 'Skills Marketplace', cs: true, diy: 'Manual', sc: false, mc: false },
  { feature: 'Automated Backups', cs: 'Daily', diy: 'Manual', sc: 'Daily', mc: 'Weekly' },
  { feature: 'Cost Monitoring', cs: true, diy: false, sc: false, mc: false },
  { feature: 'Free Trial', cs: 'Available', diy: false, sc: false, mc: '7 days' },
];

const useCases = [
  'Read & summarize email', 'Draft replies and follow-ups', 'Schedule meetings from chat', 'Remind you of deadlines',
  'Track expenses and receipts', 'Run payroll calculations', 'Generate invoices', 'Research competitors',
  'Monitor news and alerts', 'Draft social posts', 'Book travel and hotels', 'Do your taxes',
  'Write contracts and NDAs', 'Screen cold outreach', 'Find best prices online', 'Create presentations',
];

const Cell = ({ val }) => {
  if (val === true) return <Check size={16} className="text-emerald-400 mx-auto" />;
  if (val === false) return <XIcon size={16} className="text-zinc-600 mx-auto" />;
  return <span className="text-sm">{val}</span>;
};

export default function Landing() {
  const [pricing, setPricing] = useState({ plans: [], trial: null });

  useEffect(() => {
    api.getPricing()
      .then(data => setPricing(data))
      .catch(err => console.error('Failed to load pricing:', err));
  }, []);

  const { plans, trial } = pricing;
  const trialText = trial?.is_enabled ? `Start ${trial.trial_days}-Day Free Trial` : 'Get Started';

  return (
    <div className="bg-[#050505] min-h-screen">
      <SEOHead
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
        keywords={PAGE_SEO.home.keywords}
        url="/"
        faq={landingFAQ}
        breadcrumbs={[{ name: 'Home', url: '/' }]}
      />
      <JsonLdSchema 
        page="home" 
        faq={landingFAQ} 
        breadcrumbs={[{ name: 'Home', url: '/' }]} 
      />
      {/* Hero */}
      <section data-testid="hero-section" className="pt-32 pb-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(16,185,129,0.06)_0%,transparent_60%)]" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-block mb-6 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-sm">
            <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">Now in Public Beta</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-white mb-6 leading-[1.1]">
            Deploy <span className="text-emerald-400">OpenClaw</span><br />in under 60 seconds
          </h1>
          <p className="text-base md:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            Managed hosting for your AI assistant. No SSH, no server config, no DevOps.
            Just pick your model, connect your channel, and deploy.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link to="/signup" data-testid="hero-cta-primary" className="bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-8 py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_20px_rgba(16,185,129,0.3)] flex items-center gap-2">
              {trialText} <ArrowRight size={16} />
            </Link>
            <Link to="/features" data-testid="hero-cta-secondary" className="border border-zinc-700 text-zinc-300 font-mono text-sm rounded-sm px-8 py-3 hover:border-zinc-500 hover:text-white transition-all">
              View Features
            </Link>
          </div>
          <TerminalDemo />
        </div>
      </section>

      {/* Features Grid */}
      <section data-testid="features-section" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 section-marker">What It Does</h2>
          <p className="text-zinc-400 mb-16 max-w-xl">Everything you need to run OpenClaw in the cloud, without the hassle.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} data-testid={`feature-card-${i}`} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 hover:border-emerald-500/20 transition-all group">
                  <Icon size={24} className="text-emerald-400 mb-4 group-hover:scale-110 transition-transform" />
                  <h3 className="text-white font-mono font-bold mb-2">{f.title}</h3>
                  <p className="text-zinc-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section data-testid="comparison-section" className="py-24 px-4 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 section-marker">How We Compare</h2>
          <p className="text-zinc-400 mb-12">ClawStinger vs the alternatives.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="py-3 px-4 text-xs font-mono text-zinc-500 uppercase tracking-widest">Feature</th>
                  <th className="py-3 px-4 text-xs font-mono text-emerald-400 uppercase tracking-widest text-center">ClawStinger</th>
                  <th className="py-3 px-4 text-xs font-mono text-zinc-500 uppercase tracking-widest text-center">DIY</th>
                  <th className="py-3 px-4 text-xs font-mono text-zinc-500 uppercase tracking-widest text-center">SimpleClaw</th>
                  <th className="py-3 px-4 text-xs font-mono text-zinc-500 uppercase tracking-widest text-center">MyClaw</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, i) => (
                  <tr key={i} className="border-b border-zinc-800/50 hover:bg-white/[0.02]">
                    <td className="py-3 px-4 text-sm text-zinc-300 font-mono">{row.feature}</td>
                    <td className="py-3 px-4 text-center text-emerald-400 font-mono"><Cell val={row.cs} /></td>
                    <td className="py-3 px-4 text-center text-zinc-400"><Cell val={row.diy} /></td>
                    <td className="py-3 px-4 text-center text-zinc-400"><Cell val={row.sc} /></td>
                    <td className="py-3 px-4 text-center text-zinc-400"><Cell val={row.mc} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Use Cases Ticker */}
      <section data-testid="usecases-section" className="py-24 px-4 overflow-hidden">
        <div className="max-w-7xl mx-auto mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 section-marker">What Can It Do</h2>
          <p className="text-zinc-400">One assistant, thousands of use cases.</p>
        </div>
        <div className="relative">
          <div className="flex gap-4 animate-scroll-left">
            {[...useCases, ...useCases].map((u, i) => (
              <div key={i} className="flex-shrink-0 bg-[#0A0A0A] border border-zinc-800 rounded-sm px-5 py-3 text-sm font-mono text-zinc-400 whitespace-nowrap hover:border-emerald-500/30 hover:text-emerald-400 transition-all">
                {u}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section data-testid="pricing-section" className="py-24 px-4 bg-[#0A0A0A]">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 section-marker">Pricing</h2>
          <p className="text-zinc-400 mb-12">Simple, transparent pricing. {trial?.is_enabled ? 'Start with a free trial.' : 'Scale when ready.'}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
            {plans.length > 0 ? plans.map((plan, i) => (
              <div
                key={plan.id || i}
                data-testid={`pricing-${plan.name}`}
                className={`rounded-sm p-8 border transition-all flex flex-col ${plan.is_highlighted ? 'bg-emerald-500/5 border-emerald-500/30 shadow-[0_0_30px_rgba(16,185,129,0.1)]' : 'bg-[#111] border-zinc-800 hover:border-zinc-700'}`}
              >
                {plan.is_highlighted && <div className="text-xs font-mono text-emerald-400 uppercase tracking-widest mb-4">Most Popular</div>}
                <h3 className="text-white font-mono font-bold text-xl mb-1">{plan.display_name}</h3>
                <p className="text-zinc-500 text-sm mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-mono font-bold text-white">${plan.price_monthly}</span>
                  <span className="text-zinc-500 text-sm font-mono">/month</span>
                </div>
                <ul className="space-y-3 flex-grow">
                  {[
                    `${plan.max_agents} Agent${plan.max_agents > 1 ? 's' : ''}`,
                    `${plan.vcpu} vCPU`,
                    `${plan.ram_gb}GB RAM`,
                    `${plan.storage_gb}GB SSD`,
                    'RDP/SSH Access',
                    'Daily Backups',
                    'Pre-configured Integrations'
                  ].map((f, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-zinc-400">
                      <Check size={14} className="text-emerald-400 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  data-testid={`pricing-cta-${plan.name}`}
                  className={`block w-full text-center font-mono font-bold text-sm rounded-sm py-3 mt-8 transition-all click-scale ${plan.is_highlighted ? 'bg-emerald-500 text-black hover:bg-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.3)]' : 'border border-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
                >
                  {trial?.is_enabled ? trial.trial_cta_text : 'Get Started'}
                </Link>
              </div>
            )) : (
              // Loading skeleton
              [1,2,3].map(i => (
                <div key={i} className="rounded-sm p-8 border border-zinc-800 bg-[#111] animate-pulse">
                  <div className="h-6 bg-zinc-800 rounded w-1/3 mb-4"></div>
                  <div className="h-12 bg-zinc-800 rounded w-1/2 mb-6"></div>
                  <div className="space-y-2">
                    {[1,2,3,4,5].map(j => <div key={j} className="h-4 bg-zinc-800 rounded"></div>)}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section data-testid="social-proof-section" className="py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-12 section-marker">What People Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {MOCK_TESTIMONIALS.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="bg-[#0A0A0A] border border-zinc-800 rounded-sm p-6 hover:border-zinc-700 transition-all">
                <p className="text-zinc-300 text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center">
                    <span className="text-xs font-mono text-emerald-400">{t.name.charAt(0).toUpperCase()}</span>
                  </div>
                  <span className="text-xs font-mono text-zinc-500">@{t.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="final-cta-section" className="py-32 px-4 text-center relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(16,185,129,0.06)_0%,transparent_60%)]" />
        <div className="relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Ready to deploy?</h2>
          <p className="text-zinc-400 mb-8 max-w-lg mx-auto">
            {trial?.is_enabled 
              ? `Start your ${trial.trial_days}-day free trial. No credit card required.`
              : 'Get started today. Simple setup, no hassle.'}
          </p>
          <Link to="/signup" data-testid="final-cta-btn" className="inline-flex items-center gap-2 bg-emerald-500 text-black font-mono font-bold text-sm rounded-sm px-8 py-3 hover:bg-emerald-400 transition-all click-scale shadow-[0_0_20px_rgba(16,185,129,0.3)]">
            Get Started <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
